import { z } from 'zod'
import { fallback, IMAGE_CROP_SCALE_MAX, IMAGE_CROP_SCALE_MIN, imageCropDefault } from '../../../utilities'
import { colorSchema } from './common'

export const LANDING_PAGE_BANNER_ASPECT_RATIO = 16 / 9

/**
 * Widest the banner is ever displayed — the form viewer caps its wrapper at Tailwind's
 * max-w-3xl. The widest render is the one that needs the most source pixels, so this is
 * what a resolution warning has to measure against.
 */
export const LANDING_PAGE_BANNER_MAX_WIDTH_PX = 768

export type LandingPageMeta = z.infer<typeof landingPageMetaSchema>

export type UnconformedCampaignLandingPage = {
	id: string
	title: string
	subtitle: string
	body: string
	imageId: string
	meta?: Record<string, unknown>
}

// Conformed type (what we use in the UI)
export type ConformedCampaignLandingPage = Omit<UnconformedCampaignLandingPage, 'meta'> & {
	meta: LandingPageMeta
}

const fallbackData = {
	reachOverrideUrl: '',
	reachOverrideEnabled: false,
	logoBackgroundFillColor: '#ffffff',
	showLogo: true,
	forceAiGeneratedLabel: false,
	...imageCropDefault,
} as const

export const landingPageMetaSchema = z.object({
	reachOverrideUrl: z.string().or(fallback(fallbackData.reachOverrideUrl)),
	reachOverrideEnabled: z.boolean().or(fallback(fallbackData.reachOverrideEnabled)),
	logoBackgroundFillColor: colorSchema.or(fallback(fallbackData.logoBackgroundFillColor)),
	showLogo: z.boolean().or(fallback(fallbackData.showLogo)),
	// Shows the label even when no image is detected as AI-generated, e.g. for externally generated uploads.
	forceAiGeneratedLabel: z.boolean().or(fallback(fallbackData.forceAiGeneratedLabel)),
	imageScale: z.number().min(IMAGE_CROP_SCALE_MIN).max(IMAGE_CROP_SCALE_MAX)
		.or(fallback(fallbackData.imageScale)),
	imageOffsetX: z.number().min(0).max(100).or(fallback(fallbackData.imageOffsetX)),
	imageOffsetY: z.number().min(0).max(100).or(fallback(fallbackData.imageOffsetY)),
}).or(fallback(fallbackData))

export function conformLandingPage(landingPage: UnconformedCampaignLandingPage): ConformedCampaignLandingPage {
	const meta = landingPage.meta

	// safeguard for non-object input
	if (!meta || typeof meta !== 'object' || Array.isArray(meta)) {
		return {
			...landingPage,
			meta: landingPageMetaSchema.parse({}),
		}
	}

	return {
		...landingPage,
		meta: landingPageMetaSchema.parse(landingPage.meta),
	}
}
